import { COLORS } from "../config/constants";
import { on } from "../core/events";
import type { Player } from "../entities/player";
import { k } from "../core/kaplay";
import type { RunState } from "../core/runState";
import { heightAboveGround } from "../systems/air";
import type { InputIntent } from "../systems/inputMapping";

const EDGE_KEYS = ["jump", "boostToggle", "bell", "pause", "restart"] as const;

function pressedKeys(intent: InputIntent): string[] {
    return EDGE_KEYS.filter((key) => intent[key]);
}

function difficultyLine({ difficulty: d }: RunState): string {
    const f = (n: number) => n.toFixed(1);
    return `difficulty x${f(d.scale)}  trees ${f(d.treeDensity)} rocks ${f(d.rockDensity)} jumps ${f(d.jumpFrequency)} patches ${f(d.regionFrequency)}`;
}

function airAction(rider: Player): string | false {
    if (!rider.isAirborne()) return false;
    const trick = rider.tricks.active?.name ?? rider.tricks.done.join("+");
    return `AIR ${heightAboveGround(rider.air).toFixed(1)}m ${trick}`;
}

function riderLines(rider: Player, run: RunState): string[] {
    const { motion } = rider;
    const actions = [motion.pedalling && "pedal", motion.braking && "brake", motion.skidding && "SKID", motion.boostOn && "BOOST", airAction(rider)];
    return [
        `fps ${k.debug.fps().toFixed(0)}  world objs ${k.get("world").length}  draws ${k.debug.drawCalls()}  state ${rider.state}${rider.invulnerable > 0 ? " (invuln)" : ""}`,
        `speed ${motion.speed.toFixed(1)} m/s  ${(motion.speed * 3.6).toFixed(0)} km/h`,
        `heading ${motion.heading.toFixed(0)}  surface ${run.surface}  zone ${run.zone}`,
        `boost ${(motion.boost * 100).toFixed(0)}%  distance ${run.distanceM.toFixed(0)} m`,
        `doing ${actions.filter(Boolean).join(" ") || "-"}`,
        `score ${run.score.toFixed(0)}  style ${run.styleScore.toFixed(0)}  combo x${run.combo}  tricks ${run.tricksLanded}  crashes ${run.crashes}  air ${run.airtimeS.toFixed(1)}s`,
        difficultyLine(run),
    ];
}

function intentLines(intent: InputIntent, lastPress: string): string[] {
    const target = intent.targetHeading === null ? "-" : intent.targetHeading.toFixed(0);
    return [`input ${intent.device}  steer ${intent.steer}  target ${target}`, `last press ${lastPress}`];
}

export function addDebugOverlay(rider: Player, run: RunState) {
    const panel = k.add([k.rect(0, 0, { radius: 8 }), k.pos(8, 8), k.fixed(), k.layer("ui"), k.color(0, 0, 0), k.opacity(0.5)]);
    const label = k.add([k.text("", { size: 20 }), k.pos(16, 16), k.fixed(), k.layer("ui"), k.z(1), k.color(COLORS.uiText)]);
    let lastPress = "-";
    let lastEvent = "-";
    on("wipeout", ({ severity, cause }) => (lastEvent = `wipeout ${severity} (${cause})`));
    on("jump", ({ source }) => (lastEvent = `jump (${source})`));
    on("recover", () => (lastEvent = "recover"));
    on("trickComplete", ({ trick, value }) => (lastEvent = `${trick} +${value}`));
    on("land", ({ clean, airtime }) => (lastEvent = `land ${clean ? "clean" : "FAIL"} ${airtime.toFixed(2)}s`));
    on("surfaceChange", ({ surface }) => (lastEvent = `surface → ${surface}`));

    label.onUpdate(() => {
        const pressed = pressedKeys(rider.intent);
        if (pressed.length) lastPress = pressed.join(" ");
        label.text = [...riderLines(rider, run), ...intentLines(rider.intent, lastPress), `last event ${lastEvent}`].join("\n");
        panel.width = label.width + 16;
        panel.height = label.height + 16;
    });
    return panel;
}
